import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ContactBlockDsfr from "components/ContactBlockDsfr";
import ContactPhoneBlock from "components/ContactPhoneBlock";
import Wrapper from "components/LayoutFront/Wrapper";
import { buttonStyled } from "components/templatesComponents/StyledComponent";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import EulerianService from "services/EulerianService";
import ContactFormBlock from "./ContactFormBlock";

const Tab = (props) => {
  const { id, label, isFirstTabSelected = false } = props;

  const tabProps = useMemo(
    () => ({ tabIndex: isFirstTabSelected ? "0" : "-1", "aria-selected": isFirstTabSelected ? "true" : "false" }),
    [isFirstTabSelected],
  );

  if (!label) {
    return null;
  }

  return (
    <li role="presentation">
      <button type="button" id={id} className="fr-tabs__tab" role="tab" aria-controls={`${id}-panel`} {...tabProps}>
        {label}
      </button>
    </li>
  );
};

Tab.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  isFirstTabSelected: PropTypes.bool,
};

const TabPanel = (props) => {
  const { id, children, label = "", isFirstTabSelected = false, withOpacity = false } = props;

  if (!label) {
    return null;
  }

  return (
    <Box
      id={`${id}-panel`}
      className={`fr-tabs__panel${isFirstTabSelected ? " fr-tabs__panel--selected" : ""}`}
      role="tabpanel"
      aria-labelledby={id}
      tabIndex="0"
      sx={{ opacity: withOpacity ? 0.3 : 1 }}
    >
      {children}
    </Box>
  );
};

TabPanel.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  label: PropTypes.string,
  isFirstTabSelected: PropTypes.bool,
  withOpacity: PropTypes.bool,
};

const ContactFormDsfr = (props) => {
  const { page } = props;
  const { contents } = page;
  const {
    contactTitle,
    dynamicPartOnForm,
    contactUsTitle,
    contactForm = {},
    contactPhone = {},
    contactDigitalPointsTitle,
    contactDigitalPoints = {},
    reverseTabsOrder,
  } = contents;

  const { title: contactFormTitle } = contactForm;

  const { title: contactPhoneTitle } = contactPhone;

  const {
    title: digitalPointsTitle,
    description: digitalPointsDescription,
    detail: digitalPointsDetail = {},
  } = contactDigitalPoints;

  const { text, digitalPoint } = digitalPointsDetail;

  const handleClickDigitalPoint = () => {
    EulerianService.sendClick({ button: [`button::click::trouver un point numerique`] });
  };

  const bo = useMemo(() => typeof contactTitle === "object", [contactTitle]);
  const tabsIsReversed = useMemo(
    () => (bo ? reverseTabsOrder?.props?.content?.value === "true" : reverseTabsOrder),
    [bo, reverseTabsOrder],
  );
  const hasBoFormTitle = useMemo(() => contactFormTitle?.props?.value, [contactFormTitle]);
  const hasBoPhoneTitle = useMemo(() => contactPhoneTitle?.props?.value, [contactPhoneTitle]);

  return (
    <Wrapper sx={{ mt: { xs: 4, lg: 9 }, mb: { lg: 4 }, p: 2, px: { lg: 0 }, "& h2": { mb: 1 } }}>
      <h2>{contactTitle}</h2>
      {dynamicPartOnForm && <Box sx={{ "& > div": { py: 1 }, my: 3 }}>{dynamicPartOnForm}</Box>}
      <Stack
        spacing={4}
        sx={{
          position: "relative",
          ...(tabsIsReversed
            ? { "& .fr-tabs__list": { flexDirection: "row-reverse", justifyContent: "flex-end" } }
            : {}),
        }}
      >
        {(contactFormTitle || contactPhoneTitle) && (
          <div>
            <Box className="fr-text--xl" sx={{ fontWeight: 700, mb: contactFormTitle && contactPhoneTitle ? 4 : 2 }}>
              {contactUsTitle}
            </Box>
            {/* FORMULAIRE */}
            {!contactPhoneTitle && <ContactFormBlock page={page} />}
            {!contactFormTitle && <ContactPhoneBlock contactPhone={contactPhone} />}
            {contactFormTitle && contactPhoneTitle && (
              <div className="fr-tabs">
                {bo && reverseTabsOrder && (
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: "center",
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: 2,
                      "& label": { mr: 0 },
                    }}
                  >
                    {reverseTabsOrder} <p>Inverser les onglets</p>
                  </Stack>
                )}
                <ul className="fr-tabs__list" role="tablist" aria-label="Onglets de contact">
                  <Tab id="contact-by-form" label={contactFormTitle} isFirstTabSelected={!tabsIsReversed} />
                  <Tab id="contact-by-phone" label={contactPhoneTitle} isFirstTabSelected={tabsIsReversed} />
                </ul>
                <TabPanel
                  id="contact-by-phone"
                  label={contactPhoneTitle}
                  isFirstTabSelected={tabsIsReversed}
                  withOpacity={bo && !hasBoPhoneTitle}
                >
                  <ContactPhoneBlock contactPhone={contactPhone} />
                </TabPanel>
                <TabPanel
                  id="contact-by-form"
                  label={contactFormTitle}
                  isFirstTabSelected={!tabsIsReversed}
                  withOpacity={bo && !hasBoFormTitle}
                >
                  <ContactFormBlock page={page} />
                </TabPanel>
              </div>
            )}
          </div>
        )}
        {/* POINTS NUMERIQUES */}
        {contactDigitalPointsTitle && (
          <Stack spacing={3} sx={{ opacity: bo && !contactDigitalPointsTitle?.props?.value ? 0.5 : 1 }}>
            <p className="fr-text--xl fr-text--bold">{contactDigitalPointsTitle}</p>
            <ContactBlockDsfr
              leftChild={
                <Stack spacing={3}>
                  <h3 id="contactPhone">{digitalPointsTitle}</h3>
                  <p className="fr-text--lg">{digitalPointsDescription}</p>
                </Stack>
              }
              rightChild={
                <Stack spacing={3} sx={{ "& p": { width: "100%" } }}>
                  {text}
                  <Box onClick={handleClickDigitalPoint} sx={{ "& > a": buttonStyled }}>
                    {digitalPoint}
                  </Box>
                </Stack>
              }
            />
          </Stack>
        )}
      </Stack>
    </Wrapper>
  );
};

ContactFormDsfr.propTypes = {
  page: PropTypes.shape().isRequired,
};

export default ContactFormDsfr;
